import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    Award,
    Clock,
    DollarSign,
    Eye,
    FileText,
    Inbox,
    Package,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../common/httpclient';
import useIsMobileShell from '../hooks/useIsMobileShell';
import { dashboardTabPath, useAllowedDashboardTabs } from './dashboards/dashboardTabs';
import PageScaffold from '../component/pageScaffold/PageScaffold';
import StatusChip from '../component/common/StatusChip';
import AnimatedNumber from '../component/common/AnimatedNumber';
import { usePermissions } from '../hooks/usePermissions';
import MobileHome, { HomeBellIcon, HomeCustomizeIcon } from '../component/home/MobileHome';
import { useQuickActions } from '../component/layout/MobileQuickActions';
import { useAppBarAction } from '../component/layout/AppBarActions';
import { useEnterpriseConfigContext } from '../component/context/EnterpriseConfigContext';

/**
 * Painel inicial (`/home`) — dashboard real (KPIs, gráfico de vendas,
 * indicadores por módulo, tabelas "Vendas recentes"/"Ordens recentes").
 *
 * Ref: `prototipo-novo-layout-erp.html` (`#view-dashboard`, ver histórico de
 * revisões no `FRONTEND-TASKS.md` §2 — 6ª rodada: sem botões no page-head,
 * 4 KPIs, `charts-row` = gráfico de barras + 2 mini-cards de módulo
 * (Orçamentos/Produtos, sem Veículos), `split-row` = Vendas recentes +
 * Ordens recentes).
 *
 * Cobertura de dados x protótipo — tudo abaixo vem de endpoints reais já
 * existentes (nenhuma rota nova de backend):
 * - KPI "Vendas hoje" + gráfico "Vendas nos últimos N dias": um único
 *   `GET /dashboard/sale/resume-values` (mesmo endpoint de `SaleDashboard.js`)
 *   com `startDate`=N dias atrás/`endDate`=hoje — o valor de hoje/ontem (para
 *   a variação %) é lido direto de `chart_total_sale_by_day`, sem chamada
 *   extra. Segmentado 7d/30d/90d via `ToggleButtonGroup` (o `.seg` do
 *   protótipo não tem nenhum `onclick` real no HTML estático — aqui vira
 *   funcional, já que o custo é reaproveitar a mesma chamada com outro
 *   período, não uma feature nova).
 * - KPI "Ordens Hoje (faturadas)": `GET /dashboard/order/billed` (total das
 *   ordens com pagamento no dia, pela data de faturamento, sem canceladas).
 *   "Ordens de serviço abertas" (contagem "hoje") é derivado client-side da lista de
 *   `GET /orderservices` (últimos 30 dias, também usada pela tabela "Ordens
 *   recentes") — evita chamadas extras. O total "abertas" (não limitado a
 *   hoje) usa `GET /orderservices/resume` com uma janela ampla (3 anos), já
 *   que a API não tem um endpoint de "abertas agora" sem filtro de data.
 * - KPI "Total de Produtos Vendidos": `GET /dashboard/mobile` (hoje e ontem).
 * - Mini-card "Orçamentos": `GET /dashboard/quote` (mês atual) — os 3 campos
 *   do protótipo (Valor total/Aguard. aprov./Conversão) têm correspondência
 *   1:1 com `TotalAmount`/`CountAwaitingApproval`/`ConversionRate`.
 * - Mini-card "Produtos": `POST /chart/product` (mês atual, mesmo endpoint
 *   de `pages/reports/ProductReport.js`). O protótipo pede "Ativos"/"Margem
 *   média"/"Sem giro 60+d", mas a API não expõe contagem de produtos ativos
 *   nem "dias sem giro" (mesma lacuna já documentada em `ProductReport.js`)
 *   — "Margem média" usa `ProfitMargin` (correspondência real), "Sem giro"
 *   usa `CountProductNotSold` (produtos não vendidos no período, proxy mais
 *   próximo de "sem giro" sem inventar uma contagem de dias) e "Ativos" foi
 *   substituído por "Estoque baixo" (`CountProductsLowStock`), mesma troca
 *   já aprovada em `ProductReport.js` para o mesmo KPI ausente.
 * - "Vendas recentes"/"Ordens recentes": `GET /sales`/`GET /orderservices`
 *   (últimos 30 dias), 6 linhas mais recentes. A coluna "Cliente" do
 *   protótipo foi omitida em "Vendas recentes" — `salesResponse` (usado por
 *   `GET /sales`, o mesmo endpoint de `pages/sales/List.js`) não tem nome de
 *   cliente hoje, e `pages/sales/List.js` já foi redesenhada sem essa coluna
 *   pelo mesmo motivo (ver FRONTEND-TASKS.md, "Lista de vendas").
 *
 * Cada seção só é buscada/exibida se o usuário tiver a permissão do módulo
 * correspondente (mesmas chaves do menu lateral, `navConfig.js`) — mesmo
 * espírito de gate por permissão do `Home.js` anterior (grid de atalhos),
 * agora aplicado a cada bloco do dashboard em vez de a cada card de atalho.
 */

const RANGE_OPTIONS = [
    { value: 7, label: '7d' },
    { value: 30, label: '30d' },
    { value: 90, label: '90d' },
];

const RECENT_ROWS = 6;
const RECENT_WINDOW_DAYS = 30;
// Não há endpoint de "ordens abertas agora" sem filtro de data — usa uma
// janela ampla (3 anos) como proxy pragmático de "sem limite de data",
// documentado aqui em vez de resolvido ad hoc.
const OPEN_ORDERS_LOOKBACK_YEARS = 3;

function parseBRMoney(value) {
    if (value === undefined || value === null || value === '') return 0;
    const n = parseFloat(String(value).replace(/\./g, '').replace(',', '.'));
    return Number.isNaN(n) ? 0 : n;
}

function formatBRMoney(value) {
    return (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Formatadores dos valores animados (count up) — mesmo texto final dos valores estáticos.
const formatBRL = (n) => `R$ ${formatBRMoney(n)}`;
const formatPct1 = (n) => `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const formatInt = (n) => n.toLocaleString('pt-BR');

// Mesma função de `component/layout/AccountMenu.js` (nome do usuário no
// topbar) — reaproveitada aqui para a saudação do dashboard ficar com a
// mesma capitalização em toda a tela, já que `userData.name` vem cru da API
// (ex. "thiago", tudo minúsculo).
function capitalizeName(name) {
    if (!name) return '';
    return name.split(' ').map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
}

// Delta percentual (KPIs "Vendas hoje"/"Total de Produtos Vendidos") —
// deriva de dois valores reais (hoje x ontem), nunca um número estático.
function buildPercentDelta(today, yesterday) {
    if (!yesterday) {
        return today > 0 ? { direction: 'up', text: '↑ Novo hoje' } : null;
    }
    const pct = ((today - yesterday) / yesterday) * 100;
    const arrow = pct >= 0 ? '↑' : '↓';
    const text = `${arrow} ${Math.abs(pct).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
    return { direction: pct >= 0 ? 'up' : 'down', text };
}

// Delta de contagem (KPIs "Ordens Hoje (pagas)"/"Ordens de serviço abertas")
// — recebe uma diferença (pode ser negativa, ex. menos ordens pagas hoje do
// que ontem) ou uma contagem simples sempre >= 0 (ordens abertas hoje), e
// escolhe seta/tom conforme o sinal — nunca fixo, ao contrário do único
// exemplo estático do protótipo (sempre "↑").
function buildCountDelta(diff, suffix) {
    if (!diff) return null;
    const direction = diff > 0 ? 'up' : 'down';
    const arrow = diff > 0 ? '↑' : '↓';
    return { direction, text: `${arrow} ${Math.abs(diff)} ${suffix}` };
}

const TONE_COLORS = {
    blue: { bg: 'info.bg', fg: 'primary.main' },
    green: { bg: 'success.bg', fg: 'success.main' },
    amber: { bg: 'warning.bg', fg: 'warning.main' },
    red: { bg: 'error.bg', fg: 'error.main' },
};

// `.kpi` — ícone com tom semântico à esquerda, pill de variação à direita,
// valor grande em negrito, rótulo pequeno abaixo (DESIGN-SYSTEM.md §5.2).
function KpiCard({ icon, tone, value, label, delta, loading, amount, format, decimals }) {
    const colors = TONE_COLORS[tone] || TONE_COLORS.blue;
    // `amount` (número) + `format` => valor animado (count up); sem eles, `value` como está.
    const valueNode = amount !== undefined
        ? <AnimatedNumber value={amount} format={format} decimals={decimals} />
        : value;
    const isMobileShell = useIsMobileShell();
    if (isMobileShell) {
        // Mobile: card compacto (ícone 24px + variação na mesma linha, valor 17px
        // com números tabulares, rótulo 11px) para caber 2 KPIs por linha. O
        // valor quebra de linha em vez de truncar, para o monetário ficar completo.
        return (
            <Card>
                <CardContent sx={{ p: '10px', '&:last-child': { pb: '10px' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px', mb: '6px' }}>
                        <Box sx={{
                            width: 24, height: 24, borderRadius: '7px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: colors.bg, color: colors.fg,
                        }}>
                            {React.isValidElement(icon) ? React.cloneElement(icon, { size: 14 }) : icon}
                        </Box>
                        {loading ? (
                            <Skeleton variant="rounded" width={48} height={16} sx={{ borderRadius: '20px' }} />
                        ) : delta ? (
                            <Box sx={{
                                fontSize: '10.5px', fontWeight: 700, px: '6px', py: '2px', borderRadius: '20px',
                                color: delta.direction === 'down' ? 'error.main' : 'success.main',
                                bgcolor: delta.direction === 'down' ? 'error.bg' : 'success.bg',
                                whiteSpace: 'nowrap',
                            }}>
                                {delta.text}
                            </Box>
                        ) : null}
                    </Box>
                    {loading ? (
                        <Skeleton variant="text" width="80%" height={26} />
                    ) : (
                        <Typography sx={{
                            fontSize: '17px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2,
                            fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere',
                        }}>
                            {valueNode}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.25, mt: '2px' }}>
                        {label}
                    </Typography>
                </CardContent>
            </Card>
        );
    }
    return (
        <Card>
            <CardContent sx={{ p: '14px', '&:last-child': { pb: '14px' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
                    <Box sx={{
                        width: 34, height: 34, borderRadius: '10px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: colors.bg, color: colors.fg,
                    }}>
                        {icon}
                    </Box>
                    {loading ? (
                        <Skeleton variant="rounded" width={64} height={20} sx={{ borderRadius: '20px' }} />
                    ) : delta ? (
                        <Box sx={{
                            fontSize: '11.5px', fontWeight: 700, px: '8px', py: '3px', borderRadius: '20px',
                            color: delta.direction === 'down' ? 'error.main' : 'success.main',
                            bgcolor: delta.direction === 'down' ? 'error.bg' : 'success.bg',
                            whiteSpace: 'nowrap',
                        }}>
                            {delta.text}
                        </Box>
                    ) : null}
                </Box>
                {loading ? (
                    <Skeleton variant="text" width={100} height={34} />
                ) : (
                    <Typography sx={{ fontSize: '26px', fontWeight: 800, letterSpacing: '-0.02em', mb: '2px', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
                        {valueNode}
                    </Typography>
                )}
                <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                    {label}
                </Typography>
            </CardContent>
        </Card>
    );
}

// `.mini-mod-head` (cabeçalho ícone + título) seguido de uma pilha vertical
// de `summary-row` (label à esquerda, valor grande em negrito à direita,
// mesmo padrão já usado por `SummaryRow` em `pages/dashboards/SaleDashboard.js`)
// em vez do `.mod-stats` em grid 3 colunas do protótipo — o card fica
// esticado à altura do gráfico de barras vizinho (`alignItems:'stretch'` no
// `charts-row`), e um grid de 3 colunas raso deixava metade do card vazio.
// As linhas se distribuem (`justify-content: space-evenly`) por todo o
// espaço disponível em vez de ficarem coladas no rodapé.
function ModMiniCard({ icon, iconBg, iconFg, title, stats, loading }) {
    const isMobileShell = useIsMobileShell();
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: isMobileShell ? '10px 12px' : 2, '&:last-child': isMobileShell ? { pb: '8px' } : undefined, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '4px' }}>
                    <Box sx={{
                        width: 26, height: 26, borderRadius: '8px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: iconBg, color: iconFg,
                    }}>
                        {icon}
                    </Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                        {title}
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
                    {stats.map((stat, idx) => (
                        <Box key={stat.label}>
                            {idx > 0 && <Divider sx={{ borderColor: 'divider' }} />}
                            <Box
                                title={stat.title}
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, py: isMobileShell ? '6px' : '10px' }}
                            >
                                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                    {stat.label}
                                </Typography>
                                {loading ? (
                                    <Skeleton variant="text" width={54} height={22} />
                                ) : (
                                    <Typography sx={{ fontSize: isMobileShell ? '15px' : '16px', fontWeight: 800, color: stat.color || 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                                        {stat.amount !== undefined
                                            ? <AnimatedNumber value={stat.amount} format={stat.format} decimals={stat.decimals} />
                                            : stat.value}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
}

// Estado vazio compacto (DESIGN-SYSTEM.md §5.11) usado dentro das tabelas
// "Vendas recentes"/"Ordens recentes" quando o período não tem registros.
function EmptyRow({ colSpan, label }) {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} align="center" sx={{ border: 'none', py: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '50%', border: '2px dashed', borderColor: 'divider',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled',
                    }}>
                        <Inbox size={20} strokeWidth={2} />
                    </Box>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>{label}</Typography>
                </Box>
            </TableCell>
        </TableRow>
    );
}

// `.split-row .card` — cabeçalho (título + "Ver todas") e `table-wrap` com
// altura máxima + scroll interno (sem paginação). 6 linhas (uma a menos que
// o protótipo, pedido explícito do usuário) — `maxHeight` reduzido na mesma
// proporção (342px → 296px, ~1 linha de ~46px a menos).
//
// Abaixo de `sm` a tabela vira uma lista de 2 linhas por registro (sem scroll
// horizontal): cada coluna declara `mobile` = 'title' | 'sub' | 'value' |
// 'status'; colunas sem `mobile` (ex.: ícone de ver) ficam só no desktop.
function RecentTable({ title, onSeeAll, loading, rows, columns, onRowClick, emptyLabel, hideTitle }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const byRole = (row, role) => columns.filter((c) => c.mobile === role).map((c) => c.render(row));
    const mobileColumns = columns.filter((c) => c.mobile);
    return (
        <Card>
            <CardContent sx={{ p: '12px', '&:last-child': { pb: '12px' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: hideTitle ? 'flex-end' : 'space-between', mb: '10px' }}>
                    {!hideTitle && <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>}
                    <Button size="small" onClick={onSeeAll} sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'none', px: '10px' }}>
                        Ver todas
                    </Button>
                </Box>
                {isMobile ? (
                    <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                        {loading ? (
                            [1, 2, 3].map((n) => (
                                <Box key={n} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Skeleton variant="text" width="60%" />
                                    <Skeleton variant="text" width="40%" />
                                </Box>
                            ))
                        ) : rows.length === 0 ? (
                            <Typography sx={{ py: 3, textAlign: 'center', fontSize: '12.5px', color: 'text.secondary' }}>
                                {emptyLabel}
                            </Typography>
                        ) : (
                            rows.map((row, idx) => (
                                <Box
                                    key={row.__key ?? idx}
                                    onClick={() => onRowClick?.(row)}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: '10px',
                                        px: '4px',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '&:last-child': { borderBottom: 'none' },
                                        cursor: onRowClick ? 'pointer' : 'default',
                                        '&:active': { bgcolor: 'background.subtle' },
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <Box sx={{ fontSize: '13px', fontWeight: 700 }}>{byRole(row, 'title')}</Box>
                                        <Box
                                            sx={{
                                                fontSize: '11.5px',
                                                color: 'text.secondary',
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                columnGap: '6px',
                                                '& > *:not(:first-of-type)::before': { content: '"·"', mr: '6px' },
                                            }}
                                        >
                                            {mobileColumns.filter((c) => c.mobile === 'sub').map((c) => (
                                                <Box component="span" key={c.key} sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                                                    {c.render(row)}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                        <Box sx={{ fontSize: '13px', fontWeight: 700 }}>{byRole(row, 'value')}</Box>
                                        {byRole(row, 'status')}
                                    </Box>
                                </Box>
                            ))
                        )}
                    </Box>
                ) : (
                <Box sx={{ maxHeight: 296, overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        sx={{
                                            fontSize: '10.5px', fontWeight: 800, color: 'text.secondary',
                                            textTransform: 'uppercase', letterSpacing: '.04em',
                                            borderBottom: '1px solid', borderColor: 'divider',
                                            bgcolor: 'background.paper', py: '6px', px: '12px',
                                        }}
                                    >
                                        {col.header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [1, 2, 3].map((n) => (
                                    <TableRow key={n}>
                                        {columns.map((col) => (
                                            <TableCell key={col.key} sx={{ py: '9px', px: '12px', borderColor: 'divider' }}>
                                                <Skeleton variant="text" width="80%" />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : rows.length === 0 ? (
                                <EmptyRow colSpan={columns.length} label={emptyLabel} />
                            ) : (
                                rows.map((row, idx) => (
                                    <TableRow
                                        key={row.__key ?? idx}
                                        hover
                                        onClick={() => onRowClick?.(row)}
                                        sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                    >
                                        {columns.map((col) => (
                                            <TableCell key={col.key} sx={{ py: '7px', px: '12px', borderColor: 'divider', fontSize: '12.5px' }}>
                                                {col.render(row)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
                )}
            </CardContent>
        </Card>
    );
}

// Abas do mobile (grupos "módulos" e "recentes"). Cada item é
// `{ key, label, content }`; só o painel ativo é renderizado, mas os dados
// vivem no `Home` (estado já carregado) — trocar de aba não refaz chamadas.
// 0 itens: não renderiza; 1 item: renderiza o conteúdo sem barra de abas.
const MOBILE_TAB_ACCENT = '#448AFF';

function MobileTabsGroup({ items, mb }) {
    const [active, setActive] = useState(null);
    if (items.length === 0) return null;
    if (items.length === 1) return <Box sx={{ mb }}>{items[0].content}</Box>;
    // Se a aba ativa deixou de existir (permissão mudou), cai na primeira.
    const current = items.find((i) => i.key === active) || items[0];
    return (
        <Box sx={{ mb }}>
            <Tabs
                value={current.key}
                onChange={(_, value) => setActive(value)}
                variant="fullWidth"
                sx={{
                    minHeight: 44,
                    mb: 1,
                    '& .MuiTabs-indicator': { backgroundColor: MOBILE_TAB_ACCENT, height: 3 },
                }}
            >
                {items.map((item) => (
                    <Tab
                        key={item.key}
                        value={item.key}
                        label={item.label}
                        sx={{
                            minHeight: 44,
                            textTransform: 'none',
                            fontSize: '13px',
                            fontWeight: 700,
                            '&.Mui-selected': { color: MOBILE_TAB_ACCENT },
                        }}
                    />
                ))}
            </Tabs>
            {current.content}
        </Box>
    );
}

export default function Home() {
    const theme = useTheme();
    const navigate = useNavigate();
    const dashboardChips = useAllowedDashboardTabs();
    const isMobileShell = useIsMobileShell();
    const chartHeight = isMobileShell ? 130 : 170;
    const { hasPermission, hasAnyPermission, userData } = usePermissions();

    // Permissões por seção — mesmas chaves do menu lateral (navConfig.js),
    // mesmo espírito de gate por módulo do `Home.js` anterior.
    // Todos os KPIs/tabelas da Home exigem também o grupo de permissão HOME
    // (marcador `home.view`), além da permissão do módulo de cada seção.
    const canHome = hasPermission('home.view');
    // Quem tem home.view já podia listar vendas (GET /sales é liberado por home.view e sale.view)
    const canSales = canHome && hasAnyPermission(['sale.view', 'home.view']);
    const canOrders = canHome && hasPermission('serviceorder.view');
    const canQuotes = canHome && hasPermission('servicequote.view');
    const canProducts = canHome && hasPermission('product.view');

    const [barRangeDays, setBarRangeDays] = useState(7);
    const [salesLoading, setSalesLoading] = useState(true);
    const [salesChart, setSalesChart] = useState([]);
    const [salesTodayTotal, setSalesTodayTotal] = useState(0);
    const [salesYesterdayTotal, setSalesYesterdayTotal] = useState(0);
    const [recentSales, setRecentSales] = useState([]);
    const [salesTodayCount, setSalesTodayCount] = useState(null);

    const [ordersLoading, setOrdersLoading] = useState(true);
    const [recentOrders, setRecentOrders] = useState([]);
    // Valor (R$) somado das ordens com status "Paga" criadas hoje/ontem —
    // não a contagem de ordens (feedback do usuário: o KPI "Ordens Hoje
    // (pagas)" deve mostrar o valor faturado, não a quantidade).
    const [ordersPaidTodayAmount, setOrdersPaidTodayAmount] = useState(0);
    const [ordersPaidYesterdayAmount, setOrdersPaidYesterdayAmount] = useState(0);
    const [ordersOpenToday, setOrdersOpenToday] = useState(0);
    const [ordersOpenTotal, setOrdersOpenTotal] = useState(0);
    // Ordens criadas hoje (exceto canceladas) — contagem do cartão "Ordens de hoje" do mobile.
    const [ordersCountToday, setOrdersCountToday] = useState(null);

    const [quoteLoading, setQuoteLoading] = useState(true);
    const [quoteData, setQuoteData] = useState(null);

    const [productLoading, setProductLoading] = useState(true);
    const [productData, setProductData] = useState(null);

    const loadSales = useCallback(async (rangeDays) => {
        if (!canSales) {
            setSalesLoading(false);
            return;
        }
        setSalesLoading(true);
        try {
            const start = dayjs().subtract(rangeDays - 1, 'day');
            const end = dayjs();
            const [chartResponse, recentResponse] = await Promise.all([
                httpClient.get(`/dashboard/sale/resume-values?startDate=${start.format('DD/MM/YYYY')}&endDate=${end.format('DD/MM/YYYY')}`),
                httpClient.get(`/sales?startDate=${dayjs().subtract(RECENT_WINDOW_DAYS, 'day').format('DD/MM/YYYY')}&endDate=${end.format('DD/MM/YYYY')}`),
            ]);

            if (chartResponse.status === 200) {
                const byDay = chartResponse.data?.chart_total_sale_by_day || [];
                setSalesChart(byDay);
                const todayLabel = dayjs().format('DD/MM');
                const yesterdayLabel = dayjs().subtract(1, 'day').format('DD/MM');
                setSalesTodayTotal(byDay.find((d) => d.day === todayLabel)?.total || 0);
                setSalesYesterdayTotal(byDay.find((d) => d.day === yesterdayLabel)?.total || 0);
            }

            if (recentResponse.status === 200) {
                const allSales = recentResponse.data?.sales || [];
                setRecentSales(allSales.slice(0, RECENT_ROWS));
                const todayPrefix = dayjs().format('DD/MM/YYYY');
                setSalesTodayCount(allSales.filter((sale) => String(sale.created_date || '').startsWith(todayPrefix) && sale.status !== 'Cancelada').length);
            }
        } catch (error) {
            console.error('Erro ao carregar dados de vendas do dashboard:', error);
        } finally {
            setSalesLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSales]);

    const loadOrders = useCallback(async () => {
        if (!canOrders) {
            setOrdersLoading(false);
            return;
        }
        setOrdersLoading(true);
        try {
            const today = dayjs();
            const [listResponse, resumeResponse, billedResponse] = await Promise.all([
                httpClient.get(`/orderservices?startDate=${today.subtract(RECENT_WINDOW_DAYS, 'day').format('DD/MM/YYYY')}&endDate=${today.format('DD/MM/YYYY')}`),
                httpClient.get(`/orderservices/resume?startDate=${today.subtract(OPEN_ORDERS_LOOKBACK_YEARS, 'year').format('DD/MM/YYYY')}&endDate=${today.format('DD/MM/YYYY')}`),
                httpClient.get(`/dashboard/order/billed?startDate=${today.subtract(1, 'day').format('DD/MM/YYYY')}&endDate=${today.format('DD/MM/YYYY')}`),
            ]);

            if (listResponse.status === 200) {
                const orders = listResponse.data || [];
                const todayLabel = today.format('DD/MM/YYYY');
                const isOpenStatus = (status) => status === 'Não Iniciada' || status === 'Em Execução';

                setOrdersOpenToday(orders.filter((o) => isOpenStatus(o.status) && dayjs(o.created_at).format('DD/MM/YYYY') === todayLabel).length);
                setOrdersCountToday(orders.filter((o) => o.status !== 'Cancelada' && dayjs(o.created_at).format('DD/MM/YYYY') === todayLabel).length);
                setRecentOrders(orders.slice(0, RECENT_ROWS));
            }

            if (billedResponse.status === 200) {
                const billedDays = billedResponse.data?.days || [];
                const billedTotal = (dateLabel) => billedDays.find((d) => d.day === dateLabel)?.total;
                setOrdersPaidTodayAmount(parseBRMoney(billedTotal(today.format('DD/MM/YYYY'))));
                setOrdersPaidYesterdayAmount(parseBRMoney(billedTotal(today.subtract(1, 'day').format('DD/MM/YYYY'))));
            }

            if (resumeResponse.status === 200) {
                const resume = resumeResponse.data || {};
                setOrdersOpenTotal((resume.not_started || 0) + (resume.in_progress || 0));
            }
        } catch (error) {
            console.error('Erro ao carregar dados de ordens de serviço do dashboard:', error);
        } finally {
            setOrdersLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canOrders]);

    const loadQuotes = useCallback(async () => {
        if (!canQuotes) {
            setQuoteLoading(false);
            return;
        }
        setQuoteLoading(true);
        try {
            const start = dayjs().startOf('month');
            const end = dayjs();
            const response = await httpClient.get(`/dashboard/quote?startDate=${start.format('DD/MM/YYYY')}&endDate=${end.format('DD/MM/YYYY')}`);
            if (response.status === 200) {
                setQuoteData(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar dados de orçamentos do dashboard:', error);
        } finally {
            setQuoteLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canQuotes]);

    const loadProducts = useCallback(async () => {
        if (!canProducts) {
            setProductLoading(false);
            return;
        }
        setProductLoading(true);
        try {
            const start = dayjs().startOf('month');
            const end = dayjs();
            const response = await httpClient.post('/chart/product', {
                start: start.format('DD/MM/YYYY'),
                end: end.format('DD/MM/YYYY'),
            });
            if (response.status === 200) {
                setProductData(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar dados de produtos do dashboard:', error);
        } finally {
            setProductLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canProducts]);

    useEffect(() => { loadSales(barRangeDays); }, [loadSales, barRangeDays]);
    useEffect(() => { loadOrders(); }, [loadOrders]);
    useEffect(() => { loadQuotes(); }, [loadQuotes]);
    useEffect(() => { loadProducts(); }, [loadProducts]);

    // KPI "Total de Produtos Vendidos" — vem de `/dashboard/mobile` (hoje x
    // ontem), independente das demais seções de vendas (mantido em seu
    // próprio efeito porque a permissão que o guarda é a mesma de Vendas,
    // mas o endpoint e o formato de dado são outros).
    const [productsSoldToday, setProductsSoldToday] = useState(0);
    const [productsSoldYesterday, setProductsSoldYesterday] = useState(0);
    const [productsSoldLoading, setProductsSoldLoading] = useState(true);

    useEffect(() => {
        if (!canSales) {
            setProductsSoldLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            setProductsSoldLoading(true);
            try {
                const today = dayjs().format('DD/MM/YYYY');
                const yesterday = dayjs().subtract(1, 'day').format('DD/MM/YYYY');
                const [todayResponse, yesterdayResponse] = await Promise.all([
                    httpClient.get(`/dashboard/mobile?startDate=${today}&endDate=${today}`),
                    httpClient.get(`/dashboard/mobile?startDate=${yesterday}&endDate=${yesterday}`),
                ]);
                if (cancelled) return;
                if (todayResponse.status === 200) setProductsSoldToday(todayResponse.data?.total_product_sold || 0);
                if (yesterdayResponse.status === 200) setProductsSoldYesterday(yesterdayResponse.data?.total_product_sold || 0);
            } catch (error) {
                console.error('Erro ao carregar produtos vendidos do dashboard:', error);
            } finally {
                if (!cancelled) setProductsSoldLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [canSales]);

    // ---- Mobile (home.html do protótipo): dados extras só buscados no shell mobile ----
    const { settings: enterpriseSettings } = useEnterpriseConfigContext();
    const quickActions = useQuickActions();
    const canReceivable = canHome && hasPermission('accounts.receivable.view');
    const canPayable = canHome && hasPermission('accounts.payable.view');
    const canUpcoming = canHome && Boolean(enterpriseSettings?.os_use_vehicle) && hasPermission('serviceorder.upcoming');
    const [receivableToday, setReceivableToday] = useState({ loading: true, total: '0,00', count: 0 });
    const [payableTomorrow, setPayableTomorrow] = useState(null);
    const [payableToday, setPayableToday] = useState({ loading: true, total: '0,00', count: 0 });
    const [upcomingItem, setUpcomingItem] = useState({ loading: true, item: null });
    const [notifOpen, setNotifOpen] = useState(false);
    const [layoutOpen, setLayoutOpen] = useState(false);
    // Seção "Últimos orçamentos" (opcional na personalização): só busca se o usuário tem a permissão.
    const [recentQuotes, setRecentQuotes] = useState({ loading: true, items: [] });

    useEffect(() => {
        if (!isMobileShell || !canQuotes) return undefined;
        let cancelled = false;
        const today = dayjs();
        httpClient.get('/servicequotes', { params: { page: 1, page_size: 3, startDate: today.subtract(RECENT_WINDOW_DAYS, 'day').format('DD/MM/YYYY'), endDate: today.format('DD/MM/YYYY') } })
            .then((response) => {
                if (cancelled || response.status !== 200) return;
                setRecentQuotes({ loading: false, items: (response.data?.quotes || []).slice(0, 3) });
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setRecentQuotes((prev) => ({ ...prev, loading: false })); });
        return () => { cancelled = true; };
    }, [isMobileShell, canQuotes]);

    useEffect(() => {
        if (!isMobileShell || !canReceivable) return undefined;
        let cancelled = false;
        const today = dayjs().format('DD/MM/YYYY');
        httpClient.get('/transactions/receivables', { params: { page: 1, page_size: 1, start_month: today, end_month: today, status: 'pendente' } })
            .then((response) => {
                if (cancelled || response.status !== 200) return;
                setReceivableToday({ loading: false, total: response.data?.total_pending || '0,00', count: response.data?.total || 0 });
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setReceivableToday((prev) => ({ ...prev, loading: false })); });
        return () => { cancelled = true; };
    }, [isMobileShell, canReceivable]);

    useEffect(() => {
        if (!isMobileShell || !canPayable) return undefined;
        let cancelled = false;
        const tomorrow = dayjs().add(1, 'day').format('DD/MM/YYYY');
        httpClient.get('/transactions/payables', { params: { page: 1, page_size: 1, start_month: tomorrow, end_month: tomorrow, status: 'pendente' } })
            .then((response) => {
                if (cancelled || response.status !== 200) return;
                if ((response.data?.total || 0) > 0) setPayableTomorrow({ count: response.data.total, total: response.data.total_pending || '0,00' });
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [isMobileShell, canPayable]);

    // Indicador "A pagar hoje" do Resumo do dia (mesmo endpoint do "amanhã" acima).
    useEffect(() => {
        if (!isMobileShell || !canPayable) return undefined;
        let cancelled = false;
        const today = dayjs().format('DD/MM/YYYY');
        httpClient.get('/transactions/payables', { params: { page: 1, page_size: 1, start_month: today, end_month: today, status: 'pendente' } })
            .then((response) => {
                if (cancelled || response.status !== 200) return;
                setPayableToday({ loading: false, total: response.data?.total_pending || '0,00', count: response.data?.total || 0 });
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setPayableToday((prev) => ({ ...prev, loading: false })); });
        return () => { cancelled = true; };
    }, [isMobileShell, canPayable]);

    useEffect(() => {
        if (!isMobileShell || !canUpcoming) return undefined;
        let cancelled = false;
        httpClient.get('/vehicles/recurring-services/upcoming', { params: { status: 'default', page: 1, page_size: 1 } })
            .then((response) => {
                if (cancelled || response.status !== 200) return;
                const first = Array.isArray(response.data?.items) ? response.data.items[0] : null;
                setUpcomingItem({ loading: false, item: first || null });
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setUpcomingItem((prev) => ({ ...prev, loading: false })); });
        return () => { cancelled = true; };
    }, [isMobileShell, canUpcoming]);

    const openOrdersList = recentOrders.filter((o) => o.status === 'Não Iniciada' || o.status === 'Em Execução');
    const lateOrders = openOrdersList.filter((o) => o.delivery_date && dayjs(o.delivery_date).year() > 2000 && dayjs(o.delivery_date).isBefore(dayjs()));
    const lowStock = productData?.CountProductsLowStock || 0;

    const notifications = [];
    if (canOrders && lateOrders.length > 0) notifications.push({ id: 'late', tone: 'red', title: `${lateOrders.length} ${lateOrders.length === 1 ? 'ordem atrasada' : 'ordens atrasadas'}`, sub: 'Prazo de entrega vencido', path: '/serviceorders' });
    if (canProducts && lowStock > 0) notifications.push({ id: 'stock', tone: 'amber', title: `${lowStock} ${lowStock === 1 ? 'produto com estoque baixo' : 'produtos com estoque baixo'}`, sub: 'Repor estoque', path: '/products' });
    if (payableTomorrow) notifications.push({ id: 'payable', tone: 'purple', title: payableTomorrow.count === 1 ? 'Conta a pagar vence amanhã' : `${payableTomorrow.count} contas a pagar vencem amanhã`, sub: `R$ ${payableTomorrow.total}`, path: '/accounts/payable' });

    useAppBarAction({
        actions: [
            { label: 'Personalizar início', icon: HomeCustomizeIcon, onClick: () => setLayoutOpen(true) },
            { label: 'Notificações', icon: HomeBellIcon, dot: notifications.length > 0, onClick: () => setNotifOpen(true) },
        ],
        enabled: isMobileShell && canHome,
    });

    const barChartDataset = salesChart.map((d) => ({ day: d.day, total: d.total }));
    const hasBarData = barChartDataset.some((d) => d.total > 0);
    const periodTotal = salesChart.reduce((sum, d) => sum + (d.total || 0), 0);

    const greetingName = capitalizeName(userData?.name || '');
    const todayLong = dayjs().locale('pt-br').format('D [de] MMMM');

    const quoteCard = (
        <ModMiniCard
            icon={<FileText size={14} strokeWidth={2} />}
            iconBg="info.bg"
            iconFg="primary.main"
            title="Orçamentos"
            loading={quoteLoading}
            stats={[
                { label: 'Valor total', value: `R$ ${formatBRMoney(parseBRMoney(quoteData?.total_amount))}`, amount: parseBRMoney(quoteData?.total_amount), decimals: 2, format: formatBRL, title: `Valor total: R$ ${quoteData?.total_amount || '0,00'}` },
                { label: 'Aguard. aprov.', value: String(quoteData?.count_awaiting_approval || 0), amount: quoteData?.count_awaiting_approval || 0, format: String, title: `Aguardando aprovação: ${quoteData?.count_awaiting_approval || 0}` },
                { label: 'Conversão', value: `${(quoteData?.conversion_rate || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`, amount: quoteData?.conversion_rate || 0, decimals: 1, format: formatPct1, color: 'secondary.main', title: `Taxa de conversão: ${(quoteData?.conversion_rate || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` },
            ]}
        />
    );

    const productCard = (
        <ModMiniCard
            icon={<Package size={14} strokeWidth={2} />}
            iconBg="secondary.bg"
            iconFg="secondary.main"
            title="Produtos"
            loading={productLoading}
            stats={[
                { label: 'Margem média', value: `${productData?.ProfitMargin || 0}%`, title: `Margem média: ${productData?.ProfitMargin || 0}%` },
                { label: 'Sem giro', value: (productData?.CountProductNotSold || 0).toLocaleString('pt-BR'), amount: productData?.CountProductNotSold || 0, format: formatInt, title: `Sem venda no período: ${productData?.CountProductNotSold || 0}` },
                { label: 'Estoque baixo', value: (productData?.CountProductsLowStock || 0).toLocaleString('pt-BR'), amount: productData?.CountProductsLowStock || 0, format: formatInt, title: `Estoque baixo: ${productData?.CountProductsLowStock || 0}` },
            ]}
        />
    );

    // `hideTitle` = dentro de aba (o rótulo da aba já nomeia a lista).
    const recentSalesTable = (hideTitle) => (
        <RecentTable
            title="Vendas recentes"
            hideTitle={hideTitle}
            onSeeAll={() => navigate('/sales')}
            loading={salesLoading}
            rows={recentSales}
            emptyLabel="Nenhuma venda nos últimos 30 dias"
            onRowClick={(row) => navigate(`/sales/${row.id}`)}
            columns={[
                {
                    key: 'number', header: 'Pedido', mobile: 'title', render: (row) => (
                        <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 700, color: 'text.primary' }}>
                            #{row.number}
                        </Typography>
                    ),
                },
                { key: 'created_date', header: 'Data', mobile: 'sub', render: (row) => row.created_date },
                { key: 'status', header: 'Status', mobile: 'status', render: (row) => <StatusChip domain="sale" value={row.status} /> },
                {
                    key: 'final_total', header: 'Valor', mobile: 'value', render: (row) => (
                        <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            R$ {row.final_total}
                        </Typography>
                    ),
                },
                {
                    key: 'view', header: '', render: () => (
                        <Eye size={14} strokeWidth={2} color={theme.palette.text.disabled} />
                    ),
                },
            ]}
        />
    );

    const recentOrdersTable = (hideTitle) => (
        <RecentTable
            title="Ordens recentes"
            hideTitle={hideTitle}
            onSeeAll={() => navigate('/serviceorders')}
            loading={ordersLoading}
            rows={recentOrders}
            emptyLabel="Nenhuma ordem nos últimos 30 dias"
            onRowClick={(row) => navigate(`/serviceorders/${row.order_service_id}/view`)}
            columns={[
                {
                    key: 'number', header: 'Ordem', mobile: 'title', render: (row) => (
                        <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 700, color: 'text.primary' }}>
                            #{row.number}
                        </Typography>
                    ),
                },
                { key: 'customer_name', header: 'Cliente', mobile: 'sub', render: (row) => row.customer_name },
                {
                    key: 'created_at', header: 'Data', mobile: 'sub', render: (row) => new Date(row.created_at).toLocaleString('pt-BR', {
                        timeZone: 'UTC', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                    }),
                },
                { key: 'status', header: 'Status', mobile: 'status', render: (row) => <StatusChip domain="orderService" value={row.status} /> },
                {
                    // `amount_discount` é só o valor do desconto aplicado (0,00 quando
                    // não há desconto) — o total real da ordem é `amount`
                    // (`ServiceOrderListView.Amount`, vindo de `GetOrderValues`).
                    key: 'amount', header: 'Valor', mobile: 'value', render: (row) => (
                        <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            R$ {row.amount}
                        </Typography>
                    ),
                },
                {
                    key: 'view', header: '', render: () => (
                        <Eye size={14} strokeWidth={2} color={theme.palette.text.disabled} />
                    ),
                },
            ]}
        />
    );

    if (isMobileShell) {
        const compactMoney = (value) => {
            const n = parseBRMoney(value);
            return n >= 1000 ? `R$ ${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil` : `R$ ${formatBRMoney(n)}`;
        };
        // Catálogo dos 9 indicadores do Resumo do dia (o usuário escolhe até 4 em MobileHome).
        // Só entram os de módulos com permissão; MobileHome aplica seleção e ordem.
        const titles = (n) => `${n} ${n === 1 ? 'título' : 'títulos'}`;
        const kpis = [];
        if (canOrders) {
            kpis.push({ id: 'os', label: 'OS abertas', icon: 'orders', value: String(ordersOpenTotal), loading: ordersLoading, delta: `${lateOrders.length} ${lateOrders.length === 1 ? 'atrasada' : 'atrasadas'}`, deltaTone: 'down' });
            kpis.push({ id: 'os_billed', label: 'OS faturadas hoje', icon: 'checks', value: `R$ ${formatBRMoney(ordersPaidTodayAmount).split(',')[0]}`, loading: ordersLoading, delta: `ontem R$ ${formatBRMoney(ordersPaidYesterdayAmount).split(',')[0]}`, deltaTone: 'up' });
            kpis.push({ id: 'os_late', label: 'OS atrasadas', icon: 'alert', value: String(lateOrders.length), loading: ordersLoading, delta: 'prazo vencido', deltaTone: 'down' });
        }
        if (canSales) {
            kpis.push({ id: 'products_sold', label: 'Produtos vendidos', icon: 'cart', value: String(productsSoldToday), loading: productsSoldLoading, delta: `ontem ${productsSoldYesterday}`, deltaTone: productsSoldToday >= productsSoldYesterday ? 'up' : 'down' });
        }
        if (canReceivable) {
            kpis.push({ id: 'receivable', label: 'A receber hoje', icon: 'receipt', value: `R$ ${receivableToday.total.split(',')[0]}`, loading: receivableToday.loading, delta: titles(receivableToday.count), deltaTone: 'up' });
        }
        if (canPayable) {
            kpis.push({ id: 'payable', label: 'A pagar hoje', icon: 'wallet', value: `R$ ${payableToday.total.split(',')[0]}`, loading: payableToday.loading, delta: titles(payableToday.count), deltaTone: 'down' });
        }
        if (canProducts) {
            kpis.push({ id: 'stock', label: 'Estoque baixo', icon: 'package', value: String(lowStock), loading: productLoading, delta: 'produtos', deltaTone: 'down' });
        }
        if (canQuotes) {
            kpis.push({ id: 'quotes_total', label: 'Orçamentos', icon: 'quote', value: String(quoteData?.total_quotes || 0), loading: quoteLoading, delta: `${compactMoney(quoteData?.total_amount)} no mês`, deltaTone: 'up' });
            kpis.push({ id: 'quotes', label: 'Orçamentos aguardando', icon: 'clock', value: String(quoteData?.count_awaiting_approval || 0), loading: quoteLoading, delta: 'aguardando aprovação', deltaTone: 'down' });
        }
        const shortcutDefs = { sale: ['cart', 'Vender'], order: ['orders', 'Nova OS'], quote: ['quote', 'Orçamento'], customer: ['users', 'Cliente'] };
        const shortcuts = quickActions.filter((a) => shortcutDefs[a.id]).map((a) => ({ id: a.id, path: a.path, proto: shortcutDefs[a.id][0], short: shortcutDefs[a.id][1] }));
        return (
            <MobileHome
                permissions={{
                    sales: canSales,
                    orders: canOrders,
                    quotes: canQuotes,
                    dashboard: hasPermission('dashboard.sale'),
                    ordersDashboard: hasPermission('dashboard.serviceorder'),
                    salesList: hasPermission('sale.view'),
                    products: canProducts,
                    productsReport: hasPermission('report.product'),
                }}
                // Seções "Produtos vendidos" (mais vendidos do mês) e "Produtos com baixo estoque":
                // ambas vêm da mesma chamada `POST /chart/product` já feita acima (TopSellers / ProductsLowStock).
                products={{
                    loading: productLoading,
                    topSold: (productData?.TopSellers?.Labels || [])
                        .map((name, i) => ({ name, quantity: productData.TopSellers.Values?.[i] || 0 }))
                        .slice(0, 3),
                    lowStock: [...(productData?.ProductsLowStock || [])]
                        .sort((a, b) => a.ProductStock - b.ProductStock)
                        .slice(0, 3)
                        .map((p) => ({ name: p.ProductName, stock: p.ProductStock })),
                    lowStockTotal: lowStock,
                }}
                sales={{
                    loading: salesLoading,
                    today: salesTodayTotal,
                    deltaPct: salesYesterdayTotal ? ((salesTodayTotal - salesYesterdayTotal) / salesYesterdayTotal) * 100 : null,
                    countToday: salesTodayCount,
                    recent: recentSales.slice(0, 3),
                }}
                orders={{
                    loading: ordersLoading,
                    items: openOrdersList.slice(0, 3),
                    recent: recentOrders.slice(0, 3),
                    // Cartão "Ordens de hoje": valor faturado hoje (/dashboard/order/billed) x ontem + ordens criadas hoje.
                    // O valor faturado hoje inclui pagamentos de ordens antigas; sem ordens criadas hoje o cartão mostra 0.
                    today: ordersCountToday ? ordersPaidTodayAmount : 0,
                    deltaPct: ordersCountToday && ordersPaidYesterdayAmount ? ((ordersPaidTodayAmount - ordersPaidYesterdayAmount) / ordersPaidYesterdayAmount) * 100 : null,
                    countToday: ordersCountToday,
                }}
                quotes={{ loading: recentQuotes.loading, items: recentQuotes.items }}
                kpis={kpis}
                kpiStorageKey={userData?.id ? `fv:home:kpis:${userData.id}` : null}
                layoutStorageKey={userData?.id ? `fv:home:layout:${userData.id}` : null}
                layoutOpen={layoutOpen}
                onLayoutClose={() => setLayoutOpen(false)}
                shortcuts={shortcuts}
                upcoming={{
                    enabled: canUpcoming,
                    loading: upcomingItem.loading,
                    canGenerate: hasPermission('serviceorder.create'),
                    item: upcomingItem.item ? {
                        ...upcomingItem.item,
                        when: upcomingItem.item.days_remaining < 0
                            ? `Venceu há ${Math.abs(upcomingItem.item.days_remaining)} ${Math.abs(upcomingItem.item.days_remaining) === 1 ? 'dia' : 'dias'}`
                            : upcomingItem.item.days_remaining === 0 ? 'Vence hoje' : `Vence em ${upcomingItem.item.days_remaining} ${upcomingItem.item.days_remaining === 1 ? 'dia' : 'dias'}`,
                    } : null,
                }}
                notifications={notifications}
                notifOpen={notifOpen}
                onNotifClose={() => setNotifOpen(false)}
            />
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Dashboard'}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — só título/subtítulo, sem botões (6ª rodada do
                    protótipo removeu "Exportar relatório"/"Nova venda"). */}
                <Box sx={{ mb: isMobileShell ? '10px' : '18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography component="h1" sx={{ fontSize: isMobileShell ? '17px' : '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                            Bem-vindo de volta{greetingName ? `, ${greetingName}` : ''} 👋
                        </Typography>
                        <Typography sx={{ fontSize: isMobileShell ? '12px' : '13.5px', color: 'text.secondary', mt: isMobileShell ? 0.25 : 0.5 }}>
                            Aqui está um resumo do seu negócio hoje, {todayLong}.
                        </Typography>
                    </Box>
                    {/* dash-chips — atalhos para o hub /dashboards/<aba>; só as abas permitidas (a faixa some se nenhuma). */}
                    {!isMobileShell && dashboardChips.length > 0 && (
                        <Box component="nav" aria-label="Atalhos para os dashboards" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', maxWidth: '100%' }}>
                            {/* <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.disabled', mr: '2px' }}>
                                Dashboards
                            </Typography> */}
                            
                        </Box>
                    )}
                </Box>

                {/* kpi-grid — 4 cards. */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobileShell
                        ? 'repeat(2, minmax(0, 1fr))'
                        : { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
                    gap: isMobileShell ? 1 : 2,
                    mb: isMobileShell ? 1 : 2,
                }}>
                    {canSales && (
                        <KpiCard
                            tone="blue"
                            icon={<DollarSign size={18} strokeWidth={2} />}
                            value={`R$ ${formatBRMoney(salesTodayTotal)}`}
                            amount={salesTodayTotal}
                            decimals={2}
                            format={formatBRL}
                            label="Vendas hoje"
                            delta={buildPercentDelta(salesTodayTotal, salesYesterdayTotal)}
                            loading={salesLoading}
                        />
                    )}
                    {canOrders && (
                        <KpiCard
                            tone="green"
                            icon={<Award size={18} strokeWidth={2} />}
                            value={`R$ ${formatBRMoney(ordersPaidTodayAmount)}`}
                            amount={ordersPaidTodayAmount}
                            decimals={2}
                            format={formatBRL}
                            label="Ordens Hoje (faturadas)"
                            delta={buildPercentDelta(ordersPaidTodayAmount, ordersPaidYesterdayAmount)}
                            loading={ordersLoading}
                        />
                    )}
                    {canSales && (
                        <KpiCard
                            tone="amber"
                            icon={<Package size={18} strokeWidth={2} />}
                            value={String(productsSoldToday)}
                            amount={productsSoldToday}
                            format={String}
                            label="Total de Produtos Vendidos"
                            delta={buildPercentDelta(productsSoldToday, productsSoldYesterday)}
                            loading={productsSoldLoading}
                        />
                    )}
                    {canOrders && (
                        <KpiCard
                            tone="red"
                            icon={<Clock size={18} strokeWidth={2} />}
                            value={String(ordersOpenTotal)}
                            amount={ordersOpenTotal}
                            format={String}
                            label="Ordens de serviço abertas"
                            delta={buildCountDelta(ordersOpenToday, 'hoje')}
                            loading={ordersLoading}
                        />
                    )}
                </Box>

                {/* charts-row — gráfico de barras + mini-cards de módulo
                    (Orçamentos/Produtos). */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1fr 1fr', lg: '1.3fr .85fr .85fr' },
                    gap: isMobileShell ? 1 : 2,
                    mb: isMobileShell ? 1 : 2,
                    alignItems: 'stretch',
                }}>
                    {canSales && (
                        // Sem permissão em Orçamentos, o card de vendas assume a coluna
                        // que seria dele (o grid `lg` tem 3 colunas fixas); sem
                        // Orçamentos nem Produtos, ocupa a linha inteira.
                        <Card sx={!canQuotes ? { gridColumn: { lg: canProducts ? 'span 2' : 'span 3' } } : undefined}>
                            <CardContent sx={{ p: isMobileShell ? '10px 12px' : 2, '&:last-child': isMobileShell ? { pb: '10px' } : undefined }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isMobileShell ? '6px' : '12px', gap: 1, flexWrap: 'wrap' }}>
                                    <Box>
                                        <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>
                                            Vendas nos últimos {barRangeDays} dias
                                        </Typography>
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                            {salesLoading ? <Skeleton variant="text" width={140} /> : `Total no período: R$ ${formatBRMoney(periodTotal)}`}
                                        </Typography>
                                    </Box>
                                    <ToggleButtonGroup
                                        size="small"
                                        exclusive
                                        value={barRangeDays}
                                        onChange={(_, value) => value && setBarRangeDays(value)}
                                    >
                                        {RANGE_OPTIONS.map((opt) => (
                                            <ToggleButton key={opt.value} value={opt.value} sx={{ fontSize: '11.5px', px: '10px', py: '5px' }}>
                                                {opt.label}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Box>

                                <Box sx={{ height: chartHeight, width: '100%' }}>
                                    {salesLoading ? (
                                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: '100%', px: 1 }}>
                                            {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                                                <Skeleton key={n} variant="rectangular" sx={{ flex: 1, borderRadius: '4px 4px 0 0' }} height={60 + (n % 4) * 22} />
                                            ))}
                                        </Box>
                                    ) : hasBarData ? (
                                        <BarChart
                                            height={chartHeight}
                                            margin={{ top: 20, right: 8, bottom: 24, left: 8 }}
                                            borderRadius={5}
                                            dataset={barChartDataset}
                                            categoryGapRatio={0.5}
                                            hideLegend
                                            xAxis={[{
                                                scaleType: 'band',
                                                dataKey: 'day',
                                                disableLine: true,
                                                disableTicks: true,
                                                tickLabelStyle: { fontSize: 10, fontWeight: 600, fill: theme.palette.text.disabled },
                                            }]}
                                            yAxis={[{ disableLine: true, disableTicks: true }]}
                                            series={[{
                                                dataKey: 'total',
                                                label: 'Total de Vendas',
                                                valueFormatter: (value) => `R$ ${formatBRMoney(value)}`,
                                                color: 'url(#homeDashboardBarGradient)',
                                            }]}
                                            sx={{ '& .MuiChartsAxis-directionY': { display: 'none' } }}
                                        >
                                            <defs>
                                                <linearGradient id="homeDashboardBarGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={theme.palette.brand2.main} />
                                                    <stop offset="100%" stopColor={theme.palette.primary.main} />
                                                </linearGradient>
                                            </defs>
                                        </BarChart>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
                                            <Box sx={{ width: 48, height: 48, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                                                <DollarSign size={20} strokeWidth={2} />
                                            </Box>
                                            <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>Nenhuma venda no período</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {!isMobileShell && canQuotes && quoteCard}
                    {!isMobileShell && canProducts && productCard}
                </Box>

                {/* Mobile: módulos em abas (Orçamentos / Produtos). */}
                {isMobileShell && (
                    <MobileTabsGroup
                        mb={1}
                        items={[
                            canQuotes && { key: 'quotes', label: 'Orçamentos', content: quoteCard },
                            canProducts && { key: 'products', label: 'Produtos', content: productCard },
                        ].filter(Boolean)}
                    />
                )}

                {/* split-row — Vendas recentes / Ordens recentes (mobile: abas). */}
                {isMobileShell ? (
                    <MobileTabsGroup
                        mb={0}
                        items={[
                            canSales && { key: 'sales', label: 'Vendas recentes', content: recentSalesTable(true) },
                            canOrders && { key: 'orders', label: 'Ordens recentes', content: recentOrdersTable(true) },
                        ].filter(Boolean)}
                    />
                ) : (
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: 2,
                    }}>
                        {canSales && recentSalesTable(false)}
                        {canOrders && recentOrdersTable(false)}
                    </Box>
                )}
            </Box>
        </PageScaffold>
    );
}
